import {format, parseISO} from "date-fns";
import { IGetAllResponse } from '../../../components/util'
import { HttpRequest } from '../../../util/http'
import { userData } from '../../../util/userData'

export interface ICommentDetailsValue {
  branch: string
  parentLabel: string
  code1: string
  code2: string
  code3: string
  code4: string
  code5: string
  code6: string
  codes: Array<string | null>
  moods: Array<string | null>
  comment: string
  adminComment: string
  id: number
  commentId: string
  period: string
  question: string
  sat: string
  study: string
  survey: string
  qid: string
  avid: string
  periodId: string
  survey_key: number
  ps: string
  ai: string
  wow: string
  is_sales_opportunity: 0 | 1
  is_attrition_risk: 0 | 1
  segments: ISegmentValues[]
  isFlagged: number
  isHidden: number
  hasCommentHidden: boolean
}

export interface ISegmentValues {
  columnHeader: string
  columnValue: string
  value: string
}

export interface ICommentDetailsResponse extends IGetAllResponse<ICommentDetailsValue[]> {
  q: string
  stat: string
  question: string
  hideQuestionSelection?: boolean
  measure: string
  commentType: string
  segmentCategory: string
  segmentName: string
  openEndedQuestion: string
  toggleEdit: boolean
  flagEdit: boolean
  uchc: number,
  individualSurveysAccess: boolean
}

export interface ICommentCodeIndex {
  id: number
  label: string
  shortLabel: string
  longLabel: string
  mood: string
  typeLabel: string
  typeId: number
  isArchived: 0 | 1
}

export interface ICommentCodeMoods {
  Suggestion: ICommentCodeIndex[]
  Neutral: ICommentCodeIndex[]
  Compliment: ICommentCodeIndex[]
}

export interface ICommentCodesByType {
  typeName: string
  codes: ICommentCodeMoods
}

export interface ICommentCodeType {
  id: number
  label: string
}

export interface IAuditLogIndex {
  key: number
  username: string
  project: string
  ipAddress: string
  action: string
  actionDate: Date | string;
  description: string
}

export interface IKeyValue<T> {
  key: string
  value: T
}

export interface IDisplayWarning {
  hasPermission: number
}

// tslint:disable-next-line:no-empty-interface
export interface IUpdateCommentDetailResponse { } //TODO
export interface ICommentCodeMood extends IKeyValue<string> { }
export interface IParentSegments {
  avid: string
  label: string
}

export const getParentSegments = () => {
  const method = 'POST'

  return HttpRequest.fetch<IParentSegments[]>('/scripts/segments/segment_parents', { method })
}

export const getCommentCodeMoods = () => {
  const method = 'POST'

  return HttpRequest.fetch<ICommentCodeMood[]>('/scripts/commentdetails/comment_code_moods_get_all', { method })
}

export const getCommentDetails = (filters = userData.getFilters(), hideComments: boolean) => {
  const method = 'POST'
  const customFilters: any = filters
  customFilters.listOfPeriods = `(${getPeriodsArray(customFilters).join(',')})`

  const data: any = { ...customFilters, hideComments }

  return HttpRequest.fetch<ICommentDetailsResponse>('/scripts/commentdetails/get_comment_details_report', { data, method }).then((res) => {
    res.values.forEach((value) => {
      const { code1 = null, code2 = null, code3 = null, code4 = null, code5 = null, code6 = null } = value
      value.codes = [code1, code2, code3, code4, code5, code6]
    })

    return res
  })
}

export const shouldDisplayWarning = () => {
  const method = 'POST'
  const data = {}
  const endpoint = '/scripts/commentdetails/comment_code_display_warning'

  return HttpRequest.fetch<IDisplayWarning>(endpoint, { data, method })
}

export const updateCommentDetail = (commentDetailInEditMode: ICommentDetailsValue) => {
  const method = 'POST'
  const [code1Edit = null, code2Edit = null, code3Edit = null, code4Edit = null, code5Edit = null, code6Edit = null] = commentDetailInEditMode.codes
  const data = { commentDetailInEditMode, code1Edit, code2Edit, code3Edit, code4Edit, code5Edit, code6Edit }
  const endpoint = '/scripts/commentdetails/commentdetails_codes_update'

  return HttpRequest.fetch<IUpdateCommentDetailResponse>(endpoint, { data, method })
}

export const updateFlagOnComment = (commentDetail: ICommentDetailsValue, newFlag: boolean) => {
  const method = 'POST'
  const data = { commentDetail, newFlag }
  const endpoint = '/scripts/commentdetails/flag_comment'

  return HttpRequest.fetch<ICommentDetailsValue>(endpoint, { data, method })
}

export const updateHiddenStatus = (commentDetail: ICommentDetailsValue, hideCommentReason: string) => {
  const method = 'POST'
  const data = { commentDetail, hideCommentReason }
  const endpoint = '/scripts/commentdetails/hide_comment'

  return HttpRequest.fetch<ICommentDetailsValue>(endpoint, { data, method })
}

export const getCommentCodeTypes = () => {
  const endpoint = '/scripts/commentdetails/comment_code_type_get_all';
  const method = 'POST'
  const data = {}

  return HttpRequest.fetch<ICommentCodeType[]>(endpoint, { method, data })
}

export const getCommentCodes = (showAvannisCodes = true, showArchived = false) => {
  const endpoint = '/scripts/commentdetails/comment_code_get_all';
  const method = 'POST'
  const data = { showAvannisCodes, showArchived }

  return HttpRequest.fetch<ICommentCodeIndex[]>(endpoint, { method, data })
}

export const getCommentCodesByType = (showAvannisCodes = true) => {
  const endpoint = '/scripts/commentdetails/comment_code_get_by_type';
  const method = 'POST'
  const data = { showAvannisCodes }

  return HttpRequest.fetch<ICommentCodesByType[]>(endpoint, { method, data })
}

export const addCommentCode = (commentCode: ICommentCodeIndex) => {
  const endpoint = '/scripts/commentdetails/comment_code_add'
  const method = 'POST'
  const data = { commentCode }

  return HttpRequest.fetch<{ success: true, status: number }>(endpoint, { method, data }).then((res) => {
    if ('status' in res && res.status === 500) {
      throw res;
    }

    return res;
  })
}

export const updateCommentCode = (commentCode: ICommentCodeIndex) => {
  const endpoint = '/scripts/commentdetails/comment_code_update'
  const method = 'POST'
  const data = { commentCode }

  return HttpRequest.fetch<{ success: true, status: number }>(endpoint, { method, data }).then((res) => {
    if ('status' in res && res.status === 500) {
      throw res;
    }

    return res;
  })
}

export const archiveCommentCode = (commentCode: ICommentCodeIndex) => {
  const endpoint = '/scripts/commentdetails/comment_code_archive'
  const method = 'POST'
  const data = { commentCode }

  return HttpRequest.fetch<{ success: true, status: number }>(endpoint, { method, data }).then((res) => {
    if ('status' in res && res.status === 500) {
      throw res;
    }

    return res;
  })
}

export const getAuditLogs = (action?: string) => {
  const endpoint = '/scripts/users/audit_log_get_all';
  const method = 'POST'
  const data = { action }

  return HttpRequest.fetch<IAuditLogIndex[]>(endpoint, { method, data }).then((logs) => {
    return logs.map((log) => {
      if (typeof log.actionDate === "string") {
        const IsoDataString = parseISO(log.actionDate);
        log.actionDate = format(IsoDataString, 'M/d/yyyy');
      }
      return log
    })
  })
}

export const getPeriodsArray = (filters: any) => {
  const maxIteration = 5;
  let iteration = 0;
  const dateRange = filters.dateRange;
  const period = filters.period;
  let periods = [];

  if (dateRange && dateRange.length > 0 && dateRange !== "N/A") {
    const startDate = dateRange.split("-")[0];
    const endDate = dateRange.split("-")[1];

    const startMonth = parseInt(startDate.split("/")[1]);
    const startYear = parseInt(startDate.split("/")[0]);
    const endMonth = parseInt(endDate.split("/")[1]);
    const endYear = parseInt(endDate.split("/")[0]);

    let currentMonth = startMonth;
    let currentYear = startYear;

    let currentPeriod = "";

    while ((currentYear < endYear || currentMonth <= endMonth) && iteration <= maxIteration) {
      iteration++;
      currentPeriod = `'${currentYear}M${currentMonth > 9
        ? currentMonth.toString()
        : "0" + currentMonth.toString()
        }'`;
      periods = [...periods, currentPeriod];
      currentMonth++;

      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }
  } else periods.push(`'${period}'`)

  return periods;
}
